import classNames from "classnames";
import React, {
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import DayPickerInput from "react-day-picker/DayPickerInput";
import MomentLocaleUtils, {
  formatDate,
  parseDate,
} from "react-day-picker/moment";
import { useSelector } from "react-redux";

import { dayMonthYearFormat } from "shared/lib/datetime";

import Img from "@components/FileServer/Img";
import Alert from "@components/UI/Alert";

import { useOnClickOutside } from "@lib/hooks";

export function ErrorBox({ submitError, style }) {
  return submitError ? (
    <div style={style || { marginBottom: "20px", marginLeft: "132px" }}>
      <Alert type="warning">{submitError}</Alert>
    </div>
  ) : null;
}

export function Field(props: {
  required?: boolean;
  name?: string;
  id?: string;
  style?: any;
  label: string | ReactElement;
  autoFocus?: boolean;
  placeholder?: string;
  type?: string;
  value: string;
  options?: any[];
  error?: string;
  setter?: (string) => void;
  component?: ReactElement | (() => ReactElement);
  forceError?: boolean;
  className?: string;
}) {
  const {
    label,
    error,
    setter,
    component,
    required,
    forceError,
    className,
    ...input
  } = props;
  const [touched, setTouched] = useState(false);
  const { submitPressed } = useContext(FormContext);
  const showError = (touched || submitPressed) && error;
  const onChange = (newVal) => {
    setter(newVal);
  };

  useEffect(() => {
    setTouched(false);
    onChange(input.value);

    return () => {
      setTouched(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitPressed]);

  const touch = () => setTouched(true);
  const onBlur = touch;
  return (
    <div
      className={classNames("simple-input__wrapper", {
        "simple-input__wrapper--error": showError,
      })}
    >
      <div className="simple-input__inner">
        <label htmlFor={input.id} className={label && "simple-input__label"}>
          {label}
          {required && <span className="simple-input__label--required">*</span>}
        </label>
        <div className="simple-input__input-wrapper">
          {component ? (
            component({
              ...props,
              onChange,
              onBlur,
              touch,
              touched,
              className: classNames({
                "simple-input__input--filled": input.value,
              }),
            })
          ) : (
            <input
              type="text"
              className={classNames("simple-input__input", {
                "simple-input__input--filled": input.value,
              })}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              {...input}
            />
          )}
        </div>
      </div>
      {(showError || forceError) && (
        <div className="simple-input__error">{error}</div>
      )}
    </div>
  );
}

export function Select({
  className,
  placeholder,
  value,
  setter,
  options,
  showTop = false,
  ...input
}) {
  const [opened, setOpened] = useState(false);
  const [currentOption, setCurrentOption] = useState({ label: "" });
  useEffect(
    () =>
      setCurrentOption(
        options.find((opt) => opt.value === value) || { label: "" }
      ),
    [options, value]
  );
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const filteredOptions =
    opened && (!currentOption.value || value !== currentOption.value)
      ? options.filter((opt) =>
          opt.label
            .toLocaleLowerCase()
            .includes(currentOption.label.toLocaleLowerCase())
        )
      : options;
  const ref = useRef();
  useOnClickOutside(ref, () => {
    setOpened(false);
    if (!currentOption.value) setCurrentOption({ label: "" });
  });

  return (
    <div className={classNames("customSelect")} ref={ref}>
      <div
        role="button"
        className="customSelect__input-wrap"
        onClick={() => setOpened((isOpened) => !isOpened)}
      >
        <input
          autoComplete="off"
          className={classNames(
            "simple-input__input customSelect__input",
            className
          )}
          id={input.id}
          type="text"
          placeholder={placeholder}
          value={currentOption.label}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            setCurrentOption({ label: e.target.value });
            setOpened(true);
          }}
          onKeyDown={(e) => {
            switch (e.key) {
              case "Enter":
              case "Tab":
                e.stopPropagation();
                e.preventDefault();
                if (opened && filteredOptions[selectedOptionIdx]) {
                  const newVal = filteredOptions[selectedOptionIdx];
                  setter(newVal.value);
                  setCurrentOption(newVal);
                }
                setOpened(false);
                e.target.blur();
                break;
              case "ArrowUp":
                setSelectedOptionIdx(
                  (idx) => (idx - 1) % filteredOptions.length
                );
                break;
              case "ArrowDown":
                setOpened(true);
                setSelectedOptionIdx(
                  (idx) => (idx + 1) % filteredOptions.length
                );
                break;
              case "Escape":
                setOpened(false);
                break;
              default:
            }
          }}
        />
        <Img
          className={classNames("customSelect__input-arrow", {
            "customSelect__input-arrow--up": opened,
          })}
          src="/static/images/ico-arrow-down.svg"
        />
      </div>
      {opened && (
        <ul
          className={classNames("customSelect__options", {
            "customSelect__options--top": showTop,
          })}
        >
          {filteredOptions.map((opt, idx) => (
            <li key={opt.value}>
              <button
                type="button"
                className={classNames("customSelect__option", {
                  "customSelect__option--hover": selectedOptionIdx === idx,
                })}
                data-label={opt.label}
                onClick={() => {
                  setter(opt.value);
                  setOpened(false);
                }}
                onMouseEnter={() => setSelectedOptionIdx(idx)}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DateCalendarInput({
  value,
  placeholder,
  setter,
  disableDaysWhen,
  showTop = false,
  ...input
}) {
  const ref = useRef();
  const [opened, setOpened] = useState(false);
  const [inputVal] = useState(value && new Date(value));
  const lang = useSelector((state) => state.requestorConfig.currentLanguage);
  const defaultCountry = useSelector(
    (state) =>
      state.storage.frontendSettings?.dealerFrontendSettings?.defaultCountry ??
      ""
  );
  const placeholderForShow = placeholder || dayMonthYearFormat(defaultCountry);

  return (
    <div
      role="button"
      className={classNames("dateInput", { showTop })}
      onClick={() => {
        setOpened((open) => !open);
      }}
    >
      <DayPickerInput
        ref={ref}
        value={inputVal}
        placeholder={placeholderForShow}
        formatDate={formatDate}
        parseDate={parseDate}
        onDayChange={(date) => {
          // set only valid date
          if (date) {
            date.setHours(12); // avoid problems with Date and timezone
            setter(date?.toISOString()?.split("T")?.[0]);
          }
        }}
        inputProps={{
          onChange: (e) => {
            setter(e.target.value);
          },
          autoComplete: "off",
          id: input.id,
          onBlur: input.onBlur,
          className: classNames("simple-input__input", input.className),
        }}
        dayPickerProps={{
          disabledDays: { [disableDaysWhen]: new Date() },
          locale: lang,
          localeUtils: MomentLocaleUtils,
        }}
      />
      <Img
        src="/static/images/calendar.svg"
        className="dateInput__calendar-icon"
        onClick={() => {
          if (opened) {
            ref.current.hideDayPicker();
          } else {
            ref.current.showDayPicker();
          }
        }}
      />
    </div>
  );
}

type IFormContext = {
  submitPressed: boolean;
  setSubmitPressed: (boolean) => void;
};

export const FormContext = React.createContext<IFormContext>(null);

export function Form({ onSubmit, errors = {}, children }) {
  const [submitPressed, setSubmitPressed] = useState(false);

  return (
    <FormContext.Provider value={{ submitPressed, setSubmitPressed }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitPressed(true);

          const hasErrors = Object.values(errors).some((error) => !!error);
          if (hasErrors) return;

          onSubmit(e);
        }}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
}
